import { Flex, Tooltip, Typography } from "antd";
import { TheatreMasksIcon } from "./icons";
import { AVATAR_ITEM_PREVIEW_BORDER_RADIUS } from "./AvatarDetails";
import { useAntTheme } from "hooks/useAntTheme/";
import { VoiceProfileResponse } from "../../types";
import { SliderVoicePreview } from "./SliderVoicePreview";
import { SelectedItem } from "./styled";
import { useTranslate } from "#src/hooks";
import { useOrganization } from "hooks/useOrganization/";
import { getProfileNameWithCategory } from "./helpers";
import { AvatarPreviewSelectButton } from "./AvatarPreviewSelectButton";

const StyledDiv = SelectedItem("div");

interface AvatarVoicePreviewProps {
  voice?: VoiceProfileResponse;
  className?: string;
  isSelected?: boolean;
  onClick: () => void;
  currentlyPlayedVoice?: VoiceProfileResponse;
  setCurrentlyPlayedVoice: (voice?: VoiceProfileResponse) => void;
  language?: string;
}

export const AvatarVoicePreview = ({
  voice,
  className,
  isSelected,
  onClick,
  currentlyPlayedVoice,
  setCurrentlyPlayedVoice,
  language,
}: AvatarVoicePreviewProps) => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  const { organization } = useOrganization({});

  const voiceName =
    voice?.category === "Cloned"
      ? voice.name
      : getProfileNameWithCategory(voice?.name);

  const voiceLanguage = voice?.tags?.language;
  const supportsEmotions = voice?.tags?.supports_emotions === "true";
  const emotionsTooltip = supportsEmotions
    ? t("components.ContactWithPresenter.AvatarVoicePreview.supportsEmotions")
    : t(
        "components.ContactWithPresenter.AvatarVoicePreview.doesNotSupportEmotions"
      );

  const voiceLabel = voiceLanguage
    ? `${voiceName} - ${voiceLanguage.toUpperCase()}`
    : voiceName;

  return (
    <StyledDiv
      className={`${className} ${isSelected ? "selected" : ""}`}
      style={{
        borderRadius: AVATAR_ITEM_PREVIEW_BORDER_RADIUS,
        width: "100%",
      }}
    >
      <Flex
        vertical
        justify="center"
        align="center"
        style={{
          width: "100%",
          height: "100%",
          borderRadius: AVATAR_ITEM_PREVIEW_BORDER_RADIUS,
          boxSizing: "border-box",
          border: `1px solid ${theme.colorBorderSecondary}`,
          overflow: "hidden",
          padding: 10,
        }}
        onClick={onClick}
        data-testid="avatar-voice-preview-clickable"
      >
        <Flex vertical align="flex-start" style={{ width: "100%" }}>
          <Typography.Text
            style={{
              fontSize: 16,
              fontWeight: 500,
              letterSpacing: -1,
              lineHeight: 1.2,
              marginBottom: 10,
            }}
            title={voiceLabel}
            ellipsis
          >
            {voiceLabel}
          </Typography.Text>
          <Flex justify="space-between" style={{ width: "100%" }}>
            <Typography.Text>
              {t(
                "components.ContactWithPresenter.AvatarVoicePreview.voicePreview"
              )}
            </Typography.Text>
            <Tooltip title={emotionsTooltip}>
              <span style={{ display: "inline-flex" }}>
                <TheatreMasksIcon
                  size={20}
                  crossed={!supportsEmotions}
                  color={
                    supportsEmotions
                      ? theme.colorPrimary
                      : theme.colorTextDisabled
                  }
                />
              </span>
            </Tooltip>
          </Flex>
        </Flex>
        {voice && (
          <Flex
            align="center"
            justify="center"
            style={{
              width: "100%",
              minHeight: 32,
            }}
          >
            <SliderVoicePreview
              voice={voice}
              currentlyPlayedVoice={currentlyPlayedVoice}
              onClickPlay={setCurrentlyPlayedVoice}
              language={language}
              organizationId={organization?.id}
            />
          </Flex>
        )}
        <Flex justify="flex-end" style={{ width: "100%" }}>
          <AvatarPreviewSelectButton onClick={onClick} />
        </Flex>
      </Flex>
    </StyledDiv>
  );
};
